import Text from "./shared/Text";

/**
 * ProblemVsPlatformSection — two staggered white cards on a peach/pink wash.
 *
 * Left card describes the competitor's problem (prose paragraphs + a
 * "Here's the truth:" line), right card showcases Graphy's modern platform
 * story (subtitle + two heading/body pairs).
 *
 * On md+ viewports the cards are staggered vertically (left sits lower,
 * right sits higher). On small viewports they stack without offsets.
 */

export type ProblemVsPlatformData = {
  competitor: {
    nameDisplay: string;
    logoSrc?: string;
    /** When true, renders the nameDisplay text alongside the logoSrc image */
    showLabel?: boolean;
    paragraphs: string[];
    catchLine?: string;
    catchItems?: string[];
    truthLine?: { bold: string; rest: string };
  };
  graphy: {
    subtitle?: string;
    items: Array<{ heading: string; body: string }>;
  };
};

const ProblemVsPlatformSection = ({
  competitor,
  graphy,
}: ProblemVsPlatformData) => {
  return (
    <section className="relative w-full overflow-hidden bg-white">
      {/* Peach/pink wash — soft, edge-masked so it blends into neighboring sections */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse 70% 90% at 10% 100%, rgba(255, 180, 210, 0.28) 0%, rgba(255, 255, 255, 0) 75%)," +
            "radial-gradient(ellipse 70% 90% at 100% 0%, rgba(255, 210, 160, 0.25) 0%, rgba(255, 255, 255, 0) 75%)",
          maskImage:
            "linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)",
        }}
      />
      <div className="container relative z-10 mx-auto px-4 py-10 sm:px-6 md:px-8 md:py-20 lg:px-12 lg:py-24">
        <div className="mx-auto max-w-6xl">
        <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-[2fr_3fr] md:gap-10 lg:gap-14">
          {/* LEFT CARD — problem with competitor (intentionally smaller / muted) */}
          <div className="flex flex-col rounded-3xl border border-neutralBlack/5 bg-white p-6 shadow-[0_8px_24px_rgba(20,20,40,0.05)] md:scale-[0.94] md:p-7 md:opacity-90">
            <div className="flex flex-col items-center text-center">
              <Text
                as="div"
                variant="eyebrow"
                className="text-darkSlate04"
                style={{ letterSpacing: "0.6px" }}
              >
                The big problem with
              </Text>
              {competitor.logoSrc ? (
                <div className={`mt-2 flex items-center ${competitor.showLabel ? "gap-2" : ""}`}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={competitor.logoSrc} alt={competitor.nameDisplay} className="h-7 object-contain md:h-8" />
                  {competitor.showLabel && (
                    <Text
                      as="div"
                      variant="title-xl"
                      className="text-[22px] font-normal tracking-wide text-neutralBlack md:text-[28px]"
                    >
                      {competitor.nameDisplay}
                    </Text>
                  )}
                </div>
              ) : (
                <Text
                  as="div"
                  variant="title-xl"
                  className="mt-2 text-[22px] font-normal uppercase tracking-wide text-neutralBlack md:text-[28px]"
                >
                  {competitor.nameDisplay}
                </Text>
              )}
            </div>
            <div className="mt-6 flex flex-col gap-4">
              {competitor.paragraphs.map((p, idx) => (
                <Text
                  key={idx}
                  as="p"
                  variant="body-md"
                  className="text-[14px] leading-relaxed text-darkSlate04 md:text-[15px]"
                >
                  {p}
                </Text>
              ))}
              {competitor.catchLine && competitor.catchItems && competitor.catchItems.length > 0 ? (
                <div className="flex flex-col gap-2">
                  <Text
                    as="p"
                    variant="body-md"
                    className="text-[14px] font-semibold text-neutralBlack md:text-[15px]"
                  >
                    {competitor.catchLine}
                  </Text>
                  <ul className="flex flex-col gap-1.5">
                    {competitor.catchItems.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="mt-0.5 text-[#E5483C]">✕</span>
                        <Text
                          as="span"
                          variant="body-md"
                          className="text-[14px] leading-relaxed text-darkSlate04 md:text-[15px]"
                        >
                          {item}
                        </Text>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {competitor.truthLine ? (
                <Text
                  as="p"
                  variant="body-md"
                  className="text-[14px] leading-relaxed text-neutralBlack md:text-[15px]"
                >
                  <span className="font-semibold">
                    {competitor.truthLine.bold}
                  </span>{" "}
                  {competitor.truthLine.rest}
                </Text>
              ) : null}
            </div>
          </div>

          {/* RIGHT CARD — Graphy platform (highlighted, with warm glow) */}
          <div className="relative md:scale-[1.03]">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -inset-6 rounded-[40px] blur-2xl md:-inset-10 md:blur-3xl"
              style={{
                background:
                  "radial-gradient(ellipse 55% 65% at 25% 45%, rgba(255, 130, 60, 0.55) 0%, rgba(255, 255, 255, 0) 70%)," +
                  "radial-gradient(ellipse 55% 70% at 80% 65%, rgba(255, 180, 100, 0.5) 0%, rgba(255, 255, 255, 0) 72%)",
              }}
            />
            <div className="relative flex flex-col rounded-3xl border border-neutralBlack/5 bg-white p-6 shadow-[0_30px_80px_-10px_rgba(255,130,60,0.38),0_10px_30px_rgba(20,20,40,0.08)] md:p-12 lg:p-14">
            <div className="flex flex-col items-center text-center">
              <Text
                as="div"
                variant="eyebrow"
                className="text-darkSlate04"
                style={{ letterSpacing: "0.6px" }}
              >
                {graphy.subtitle ?? "The modern creator platform"}
              </Text>
              <img
                src="/images/graphy_logo_black.svg"
                alt="Graphy"
                className="mt-2 h-7 md:h-9"
              />
            </div>
            <div className="mt-6 flex flex-col gap-6">
              {graphy.items.map((item, idx) => (
                <div key={idx}>
                  <Text
                    as="h3"
                    variant="title-sm"
                    className="text-[16px] font-semibold text-neutralBlack md:text-[17px]"
                  >
                    {item.heading}
                  </Text>
                  <Text
                    as="p"
                    variant="body-md"
                    className="mt-2 text-[14px] leading-relaxed text-darkSlate04 md:text-[15px]"
                  >
                    {item.body}
                  </Text>
                </div>
              ))}
            </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>
  );
};

export default ProblemVsPlatformSection;
